import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import Webcam from 'react-webcam';
import { apiService } from '../../services/apiService';
import {
  MicrophoneIcon,
  StopIcon,
  ForwardIcon,
  CheckIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';

const InterviewRoom = () => {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const webcamRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);

  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [interviewStarted, setInterviewStarted] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [submittedAnswers, setSubmittedAnswers] = useState(new Set());
  const [isSubmittingAudio, setIsSubmittingAudio] = useState(false);

  // Get interview info (before starting)
  const { data: interviewInfo, isLoading: infoLoading, error } = useQuery({
    queryKey: ['interview-info', interviewId],
    queryFn: () => apiService.getCandidateInterviewInfo(interviewId),
    enabled: !!interviewId,
  });

  // Get current question (only when interview is active)
  const { data: questionData, refetch: refetchQuestion, error: questionError, isLoading: questionLoading } = useQuery({
    queryKey: ['current-question', interviewId, currentQuestionIndex],
    queryFn: () => apiService.getCandidateQuestion(interviewId, currentQuestionIndex),
    enabled: interviewStarted && !!interviewId, // ✅ Always enabled when interview started
    retry: 2,
    onSuccess: (data) => {
      console.log('Question fetched successfully via useQuery:', data);
      if (data) {
        setCurrentQuestion(data);
      }
    },
    onError: (error) => {
      console.log('Question fetch error via useQuery:', error);
      if (error.status_code === 404) {
        console.log('No more questions - completing interview');
        handleCompleteInterview();
      } else {
        console.log('Question fetch failed, trying to complete interview');
        toast.error('Unable to load more questions. Completing interview.');
        handleCompleteInterview();
      }
    },
  });

  // Update question when questionData changes
  useEffect(() => {
    if (questionData) {
      console.log('Setting current question from useQuery data:', questionData);
      setCurrentQuestion(questionData);
    }
  }, [questionData]);

  // Get progress
  const { data: progressData, refetch: refetchProgress } = useQuery({
    queryKey: ['interview-progress', interviewId],
    queryFn: () => apiService.getCandidateInterviewProgress(interviewId),
    enabled: interviewStarted && !!interviewId,
  });

  // Auto-start if already in progress
  useEffect(() => {
    const checkInterviewState = async () => {
      // ✅ Direct access - no .data needed
      if (interviewInfo?.session_status === 'in_progress' && !interviewStarted) {
        console.log('Auto-starting interview - status is in_progress');
        setInterviewStarted(true);
        
        try {
          const progressResponse = await apiService.getCandidateInterviewProgress(interviewId);
          console.log('Progress on auto-start:', progressResponse);
          
          // ✅ Direct access - progressResponse is already unwrapped
          const currentIndex = progressResponse?.current_question || 0;
          setCurrentQuestionIndex(currentIndex);
          
          // Fetch the current question directly
          const questionResponse = await apiService.getCandidateQuestion(interviewId, currentIndex);
          if (questionResponse) {
            setCurrentQuestion(questionResponse);
          }
        } catch (error) {
          console.log('Could not fetch progress on auto-start:', error);
          setCurrentQuestionIndex(0);
        }
      }
    };
    
    checkInterviewState();
  }, [interviewInfo, interviewStarted, interviewId]);

  // Start interview mutation
  const startInterviewMutation = useMutation({
    mutationFn: () => apiService.startInterview(interviewId),
    onSuccess: async (response) => {
      console.log('Start interview success response:', response);
      setInterviewStarted(true);
      setCurrentQuestionIndex(0);
      toast.success('Interview started!');
      
      // Wait a moment for the interview state to update, then fetch first question
      setTimeout(async () => {
        try {
          console.log('Fetching first question after start...');
          const questionResponse = await apiService.getCandidateQuestion(interviewId, 0);
          console.log('First question response:', questionResponse);
          
          if (questionResponse) {
            setCurrentQuestion(questionResponse);
          }
          
          // Also refresh progress
          refetchProgress();
        } catch (error) {
          console.log('Failed to fetch first question after start:', error);
          // Fallback: try refetching
          refetchQuestion();
        }
      }, 500); // Give backend time to update interview status
    },
    onError: (error) => {
      console.log('Start interview error:', error);
      if (error.status_code === 400 && error.message?.includes('already started')) {
        setInterviewStarted(true);
        toast.info('Continuing existing interview');
        
        // If already started, fetch current question
        setTimeout(async () => {
          try {
            const progressResponse = await apiService.getCandidateInterviewProgress(interviewId);
            const currentIndex = progressResponse?.current_question || 0;
            setCurrentQuestionIndex(currentIndex);
            
            const questionResponse = await apiService.getCandidateQuestion(interviewId, currentIndex);
            if (questionResponse) {
              setCurrentQuestion(questionResponse);
            }
          } catch (fetchError) {
            console.log('Failed to fetch question after error:', fetchError);
          }
        }, 100);
      } else {
        toast.error(error.message || 'Failed to start interview');
      }
    },
  });

  // Submit answer mutation
  const submitAnswerMutation = useMutation({
    mutationFn: (data) => apiService.submitCandidateAnswer(interviewId, data),
    onSuccess: (response) => {
      console.log('=== SUBMIT SUCCESS ===', response);
      toast.success('Answer submitted!');
      setSubmittedAnswers(prev => new Set([...prev, currentQuestion?.id]));
      setTranscript('');
      refetchProgress();
    },
    onError: (error) => {
      console.log('=== SUBMIT ERROR ===', error);
      toast.error(error.message || 'Failed to submit answer');
    },
  });

  // Complete interview mutation
  const completeInterviewMutation = useMutation({
    mutationFn: () => apiService.completeInterview(interviewId),
    onSuccess: () => {
      toast.success('Interview completed!');
      navigate('/candidate/interviews');
    },
    onError: (error) => {
      toast.error(error.message || 'Failed to complete interview');
    },
  });

  // Recording timer
  useEffect(() => {
    let interval = null;
    if (isRecording) {
      interval = setInterval(() => {
        setRecordingTime(time => time + 1);
      }, 1000);
    } else {
      setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  // Start recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      chunksRef.current = [];

      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunksRef.current.push(event.data);
        }
      };

      mediaRecorderRef.current.start();
      setIsRecording(true);
      setRecordingTime(0);
    } catch (error) {
      toast.error('Failed to start recording. Please check microphone permissions.');
    }
  };

  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(chunksRef.current, { type: 'audio/wav' });
        console.log('Audio blob created:', audioBlob.size, 'bytes');
        
        // Submit immediately
        handleSubmitAnswer(audioBlob);
      };
  
      // Stop all tracks
      if (mediaRecorderRef.current.stream) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    }
  };

  const handleSubmitAnswer = async (audioBlob = null) => {
    if (!currentQuestion) {
      console.error('No current question available');
      return;
    }
  
    try {
      console.log('=== SUBMITTING ANSWER ===');
      console.log('Question ID:', currentQuestion.id);
      console.log('Has audio:', !!audioBlob);
  
      if (audioBlob) {
        setIsSubmittingAudio(true);
        
        // Submit with audio using FormData
        const formData = new FormData();
        formData.append('question_id', currentQuestion.id);
        formData.append('answer_text', transcript || 'Audio response provided');
        formData.append('transcript', transcript || '');
        formData.append('auto_transcribe', 'true');
        formData.append('audio', audioBlob, `answer_${currentQuestion.id}.wav`);
        
        // Use the apiService method
        const data = await apiService.submitCandidateAnswerWithSpeech(interviewId, formData);
        console.log('Audio submission success:', data);
      } else {
        // Submit text only
        await submitAnswerMutation.mutateAsync({
          question_id: currentQuestion.id,
          answer_text: transcript || 'No answer provided'
        });
      }
      
      // Success - move to next question
      toast.success('Answer submitted successfully!');
      setSubmittedAnswers(prev => new Set([...prev, currentQuestion.id]));
      setTranscript('');
      handleNextQuestion();
      refetchProgress();
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(`Failed to submit: ${error.message}`);
    } finally {
      setIsSubmittingAudio(false);
    }
  };

  // Move to next question
  const handleNextQuestion = () => {
    const nextIndex = currentQuestionIndex + 1;
    
    // ✅ Clean direct access - no nested .data
    const totalQuestions = progressData?.total_questions || 
                          interviewInfo?.total_questions || 
                          0;
    
    console.log('=== NEXT QUESTION DEBUG ===');
    console.log('Current index:', currentQuestionIndex);
    console.log('Next index:', nextIndex);
    console.log('Progress total questions:', progressData?.total_questions);
    console.log('Interview info total questions:', interviewInfo?.total_questions);
    console.log('Final total questions:', totalQuestions);
    console.log('Will complete? (nextIndex >= totalQuestions):', nextIndex >= totalQuestions);
    console.log('Comparison:', nextIndex, '>=', totalQuestions, '=', nextIndex >= totalQuestions);
    
    if (nextIndex < totalQuestions) {
      console.log('Moving to next question:', nextIndex);
      
      // Clear current question first, then update index
      setCurrentQuestion(null);
      setCurrentQuestionIndex(nextIndex);
      
      // Clear the submitted answers for the next question
      // Note: Don't clear the entire set, just ensure we're ready for the next question
      
    } else {
      console.log('COMPLETING INTERVIEW - no more questions');
      handleCompleteInterview();
    }
  };

  // Complete interview
  const handleCompleteInterview = () => {
    console.log('Completing interview');
    completeInterviewMutation.mutate();
  };

  // Skip question
  const handleSkipQuestion = () => {
    const answerData = {
      question_id: currentQuestion.id,
      answer_text: 'Question skipped'
    };
    submitAnswerMutation.mutate(answerData);
  };

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (infoLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Interview Not Found</h2>
          <p className="text-gray-600 mb-4">The interview you're looking for doesn't exist or you don't have permission to access it.</p>
          <button
            onClick={() => navigate('/candidate')}
            className="btn-primary"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ✅ Clean direct access - data is already unwrapped by API service
  const interviewData = interviewInfo;
  const progress = progressData;

  // Debug logging
  console.log('Interview Data:', interviewData);
  console.log('Progress Data:', progress);
  console.log('Interview Started:', interviewStarted);
  console.log('Can Start:', interviewData?.can_start);
  console.log('Session Status:', interviewData?.session_status);

  // Determine button state and text based on session status
  const getInterviewButtonState = () => {
    const status = interviewData?.session_status;
    const canStart = interviewData?.can_start;

    if (status === 'scheduled' && canStart) {
      return {
        disabled: false,
        text: 'Start Interview',
        action: () => startInterviewMutation.mutate()
      };
    } else if (status === 'in_progress') {
      return {
        disabled: false,
        text: 'Continue Interview',
        action: () => {
          console.log('Continuing interview - setting as started');
          setInterviewStarted(true);
          
          setTimeout(async () => {
            try {
              const progressResponse = await apiService.getCandidateInterviewProgress(interviewId);
              const currentIndex = progressResponse?.current_question || 0;
              setCurrentQuestionIndex(currentIndex);
              
              const questionResponse = await apiService.getCandidateQuestion(interviewId, currentIndex);
              if (questionResponse) {
                setCurrentQuestion(questionResponse);
              }
            } catch (error) {
              console.log('Could not fetch current question:', error);
              setCurrentQuestionIndex(0);
            }
          }, 100);
        }
      };
    } else if (status === 'completed') {
      return {
        disabled: true,
        text: 'Interview Completed',
        action: null
      };
    } else {
      return {
        disabled: true,
        text: 'Interview Not Available',
        action: null
      };
    }
  };

  const buttonState = getInterviewButtonState();

  // Pre-interview screen
  if (!interviewStarted) {
    return (
      <div className="interview-room">
        <div className="min-h-screen flex items-center justify-center p-4">
          <div className="max-w-2xl w-full">
            <div className="question-card text-center">
              <h1 className="text-3xl font-bold text-white mb-6">{interviewData?.title}</h1>
              <p className="text-xl text-white/80 mb-8">{interviewData?.description}</p>
              
              <div className="bg-white/20 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-medium text-white mb-4">Interview Details:</h3>
                <div className="text-white/90 space-y-2">
                  <p>Duration: {interviewData?.duration_minutes} minutes</p>
                  <p>Questions: {interviewData?.total_questions}</p>
                  <p>Status: <span className={`font-semibold ${
                    interviewData?.session_status === 'scheduled' ? 'text-blue-300' :
                    interviewData?.session_status === 'in_progress' ? 'text-yellow-300' :
                    interviewData?.session_status === 'completed' ? 'text-green-300' :
                    'text-gray-300'
                  }`}>{interviewData?.session_status?.toUpperCase()}</span></p>
                </div>
              </div>

              <div className="mb-8">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  className="w-64 h-48 rounded-lg mx-auto border-4 border-white/30"
                  mirrored={true}
                />
              </div>

              <button
                onClick={() => {
                  if (buttonState.action) {
                    buttonState.action();
                  }
                }}
                disabled={startInterviewMutation.isLoading || buttonState.disabled}
                className={`px-8 py-3 rounded-lg font-medium text-lg transition-colors duration-200 disabled:opacity-50 ${
                  buttonState.disabled 
                    ? 'bg-gray-600 cursor-not-allowed' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white`}
              >
                {startInterviewMutation.isLoading ? 'Starting...' : buttonState.text}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Check if we have current question
  if (interviewStarted && (questionLoading || !currentQuestion)) {
    console.log('Showing loading state:', {
      interviewStarted,
      questionLoading,
      currentQuestion: !!currentQuestion,
      currentQuestionIndex
    });
    
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading question {currentQuestionIndex + 1}...</p>
        </div>
      </div>
    );
  }

  // ✅ Clean access to totals
  const totalQuestions = progress?.total_questions || interviewData?.total_questions || 0;
  const isLastQuestion = (currentQuestionIndex + 1) >= totalQuestions;
  const isAnswerSubmitted = submittedAnswers.has(currentQuestion?.id);
  
  // Debug the last question logic
  console.log('=== LAST QUESTION LOGIC DEBUG ===');
  console.log('Current question index:', currentQuestionIndex);
  console.log('Total questions:', totalQuestions);
  console.log('Current question index + 1:', currentQuestionIndex + 1);
  console.log('Is last question calculation:', (currentQuestionIndex + 1) >= totalQuestions);
  console.log('Is last question result:', isLastQuestion);

  // Debug button state
  console.log('=== BUTTON STATE DEBUG ===');
  console.log('Current question ID:', currentQuestion?.id);
  console.log('Submitted answers:', Array.from(submittedAnswers));
  console.log('Is answer submitted:', isAnswerSubmitted);
  console.log('Total questions:', totalQuestions);
  console.log('Is last question:', isLastQuestion);

  return (
    <div className="interview-room">
      <div className="min-h-screen p-4">
        {/* Recording indicator */}
        {isRecording && (
          <div className="recording-indicator">
            <div className="w-3 h-3 bg-white rounded-full animate-pulse"></div>
            <span>Recording - {formatTime(recordingTime)}</span>
          </div>
        )}

        {/* Progress bar */}
        <div className="absolute top-4 left-4 right-4">
          <div className="bg-white/20 rounded-full h-2">
            <div
              className="bg-green-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${progress?.progress_percentage || ((currentQuestionIndex + 1) / totalQuestions * 100) || 0}%` }}
            />
          </div>
          <p className="text-white/80 text-sm mt-2">
            Question {currentQuestionIndex + 1} of {totalQuestions}
          </p>
        </div>

        {/* Main content */}
        <div className="flex items-center justify-center min-h-screen pt-1 pb-2">
          <div className=" w-full grid grid-cols-2 lg:grid-cols-3 gap-3">
            {/* Video feed */}
            <div className="h-full">
              <div className="question-card">
                <Webcam
                  ref={webcamRef}
                  audio={false}
                  className="w-[550px] h-[550px] object-cover aspect-[4/3] rounded-lg"
                  mirrored={true}
                />
              </div>
            </div>

            {/* Question and controls */}
            <div className="lg:col-span-2">
              <div className="question-card">
                <div className="mb-6">
                  <h2 className="text-2xl font-bold text-white mb-4">
                    {currentQuestion?.question_text}
                  </h2>
                  <p className="text-white/70 text-sm">
                    Type: {currentQuestion?.question_type}
                    {currentQuestion?.skill_assessed && (
                      <span> • Skill: {currentQuestion.skill_assessed}</span>
                    )}
                  </p>
                </div>

                {/* Transcript input - COMMENTED OUT AS REQUESTED */}
                {/* <div className="mb-6">
                  <textarea
                    value={transcript}
                    onChange={(e) => setTranscript(e.target.value)}
                    placeholder="Your answer will be transcribed here, or you can type notes..."
                    className="w-full h-24 px-4 py-2 rounded-lg bg-white/10 border border-white/20 text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-white/30 disabled:opacity-50"
                    disabled={isAnswerSubmitted || isSubmittingAudio}
                  />
                </div> */}

                {/* Recording controls - TEXT SUBMIT OPTION REMOVED */}
                {!isAnswerSubmitted && (
                  <div className="flex items-center justify-center space-x-4 mb-8">
                    {!isRecording ? (
                      <button
                        onClick={startRecording}
                        disabled={isSubmittingAudio}
                        className="flex items-center space-x-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <MicrophoneIcon className="h-5 w-5" />
                        <span>Start Recording</span>
                      </button>
                    ) : (
                      <button
                        onClick={stopRecording}
                        disabled={isSubmittingAudio}
                        className="flex items-center space-x-2 bg-gray-600 hover:bg-gray-700 text-white px-6 py-3 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <StopIcon className="h-5 w-5" />
                        <span>{isSubmittingAudio ? 'Processing...' : 'Stop & Submit'}</span>
                      </button>
                    )}
                  </div>
                )}

                {/* Audio submission loading indicator */}
                {isSubmittingAudio && (
                  <div className="flex items-center justify-center space-x-3 mb-6 text-white">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span className="text-sm">Submitting please wait...</span>
                  </div>
                )}

                {/* Navigation controls */}
                {isAnswerSubmitted && (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2 text-green-300">
                      <CheckIcon className="h-5 w-5" />
                      <span>Answer submitted</span>
                    </div>

                    <div className="flex space-x-3">
                      {isLastQuestion ? (
                        <button
                          onClick={handleCompleteInterview}
                          disabled={completeInterviewMutation.isLoading}
                          className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200 disabled:opacity-50"
                        >
                          <CheckIcon className="h-5 w-5" />
                          <span>Complete Interview</span>
                        </button>
                      ) : (
                        <button
                          onClick={handleNextQuestion}
                          className="flex items-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-medium transition-colors duration-200"
                        >
                          <span>Next Question</span>
                          <ForwardIcon className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {/* Skip option for unanswered questions */}
                {!isAnswerSubmitted && (
                  <div className="flex justify-center mt-4">
                    <button
                      onClick={handleSkipQuestion}
                      disabled={submitAnswerMutation.isLoading || isSubmittingAudio}
                      className="text-white/60 hover:text-white text-sm px-4 py-2 rounded transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmittingAudio ? 'Processing...' : 'Skip Question'}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewRoom;